import React, {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { Button, IconButton, ToggleSwitch } from '@americanexpress/dls-react';
import { IconClose, IconCheck } from '@americanexpress/dls-icons';
import { useAnalyticsContext } from '@/context/analyticsContext';
import { cn } from '@/helpers/utils';
import { createPortal } from 'react-dom';
import styles from './FilterMenu.module.scss';
import touchTargetStyles from './TargetTouchSize.module.scss';

export const FilterMenu = ({
  TriggerIcon,
  triggerTitle,
  children,
  isFilterApplied,
  onResetClick,
  onApplyClick,
  onMenuClose,
  applyButtonText,
  resetButtonText,
  menuTitle,
}) => {
  const DESKTOP_MENU_MEDIA_QUERY = '(min-width: 800px)';
  const DESKTOP_MENU_MAX_HEIGHT_PX = 576;
  const VIEWPORT_BOTTOM_GAP_PX = 8;

  const { customTrack } = useAnalyticsContext();
  const [menuOpen, setMenuOpen] = useState(false);
  const [anchorStyle, setAnchorStyle] = useState({});

  const menuRef = useRef(null);
  const triggerButtonRef = useRef(null);
  const triggerIconButtonRef = useRef(null);

  const filterTriggerTitle = triggerTitle
    ? triggerTitle === 'filters'
      ? 'Main '
      : `${triggerTitle} `
    : '';

  const updateDesktopMenuAnchorStyle = useCallback(() => {
    const triggerRef = triggerButtonRef.current
      ? triggerButtonRef
      : triggerIconButtonRef;

    if (!triggerRef.current) return;
    if (!window.matchMedia(DESKTOP_MENU_MEDIA_QUERY).matches) {
      setAnchorStyle({});
      return;
    }

    const rect = triggerRef.current.getBoundingClientRect();
    const availableHeight =
      window.innerHeight - rect.bottom - VIEWPORT_BOTTOM_GAP_PX;
    const maxHeight = Math.max(
      0,
      Math.min(DESKTOP_MENU_MAX_HEIGHT_PX, availableHeight)
    );

    setAnchorStyle({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      maxHeight: `${maxHeight}px`,
    });
  }, []);

  useLayoutEffect(() => {
    if (!menuOpen) return;

    updateDesktopMenuAnchorStyle();
  }, [menuOpen, updateDesktopMenuAnchorStyle]);

  useEffect(() => {
    if (!menuOpen) return () => {};

    const handleResize = () => {
      updateDesktopMenuAnchorStyle();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [menuOpen, updateDesktopMenuAnchorStyle]);

  const appRootRef = useRef(null);

  // locate app root (area to make inert/aria-hidden)
  useEffect(() => {
    appRootRef.current = document.querySelector('#root');
  }, []);

  // manage inert / aria-hidden + body scroll while open
  useEffect(() => {
    const node = appRootRef.current;
    if (!node) return () => {};

    if (menuOpen) {
      // set inert if supported
      try {
        if ('inert' in HTMLElement.prototype) {
          node.inert = true;
        } else {
          node.setAttribute('aria-hidden', 'true');
        }
      } catch (err) {
        node.setAttribute('aria-hidden', 'true');
      }
    } else {
      // restore
      try {
        if ('inert' in HTMLElement.prototype) {
          node.inert = false;
        } else {
          node.removeAttribute('aria-hidden');
        }
      } catch (err) {
        node.removeAttribute('aria-hidden');
      }
    }
    // cleanup on unmount
    return () => {
      try {
        if ('inert' in HTMLElement.prototype) node.inert = false;
        else node.removeAttribute('aria-hidden');
      } catch (e) {
        node.removeAttribute('aria-hidden');
      }
    };
  }, [menuOpen]);

  // track previous open state
  const prevOpenRef = useRef(null);

  // stable id for this menu instance so menus can notify each other
  const menuId = useRef(`filtermenu-${menuTitle}`);

  const handleToggleMenu = useCallback(
    // eslint-disable-next-line complexity -- pre-existing complexity, refactor separately
    (e, bool, customTrackingDetails = {}) => {
      const trackingDetails = {
        detail: `${bool ? 'open' : 'close'} ${filterTriggerTitle}filter`,
      };

      if (bool) {
        // Filter menus are opened by clicking one of the filter buttons
        trackingDetails.element = `${filterTriggerTitle}filter button`;
      } else {
        // NOTE: in unit tests, some component rendering
        // methods don't include the tagName property but
        // they do include the type property, so we use
        // both target "tagName" and "type" to address that.
        const trackedElement = `${e.target.tagName}_${e.target.type}`
          .toLowerCase()
          .includes('button')
          ? e.target
          : e.target.closest && e.target.closest('button');

        // Filter menus can be closed many ways: key press, clicking outside of
        // the currently open menu, clicking the close, or the apply button, etc.
        // if the menu's close button is clicked, the "target" may be the button itself
        // or it may be any of the child html elements. If it's not the button then we
        // can search for the parent button to get the label so we know what was clicked
        trackingDetails.element = trackedElement?.ariaLabel
          ? `${trackedElement?.ariaLabel} button`
          : 'unknown';
      }

      const { detail, element } =
        Object.keys(customTrackingDetails).length > 0
          ? customTrackingDetails
          : trackingDetails;

      // if keyboard triggered close (Escape), prevent default browser behavior
      if (e?.type === 'keydown') e.preventDefault();
      // Allow explicit close calls (bool === false) regardless of event type.
      // Opening still requires activation (click / Enter / Space).
      if (bool === false) {
        // Closing Filter Menu
        setMenuOpen(false);
        onMenuClose();
      } else if (
        e?.type === 'click' ||
        e?.key === 'Enter' ||
        e?.key === ' ' ||
        e?.key === 'Spacebar'
      ) {
        // Open filter menu only on Click, Enter key, or Spacebar events

        // Notify other menus so they can close
        window.dispatchEvent(
          new CustomEvent('filterMenu:open', { detail: { id: menuId.current } })
        );

        setMenuOpen(true);
      }

      customTrack({
        event: e,
        detail,
        element,
      });
    },
    [customTrack, filterTriggerTitle, onMenuClose]
  );

  useEffect(() => {
    if (!menuOpen) {
      // Return focus to trigger button when menu closes
      if (prevOpenRef.current === true && !menuOpen) {
        // ensure menu is removed from DOM first
        requestAnimationFrame(() => {
          if (triggerButtonRef.current) {
            triggerButtonRef.current.focus();
          } else if (triggerIconButtonRef.current) {
            triggerIconButtonRef.current.focus();
          }
        });
      }

      // Only attach listeners when menu is open
      // Return a no-op cleanup so the effect consistently returns a function
      return () => {};
    }
    prevOpenRef.current = menuOpen;
    const prevOverflow = document.body.style.overflow;
    if (menuRef.current) {
      // Focus the menu when it opens
      document.body.style.overflow = 'hidden';

      requestAnimationFrame(() => {
        menuRef.current?.focus({ preventScroll: true });
      });
    }

    function handleEvents(e) {
      // Close on ESC
      if (e.type === 'keydown' && e.key === 'Escape') {
        handleToggleMenu(e, false, {
          detail: 'close filter',
          element: 'escape button',
        });
        return;
      }

      // Close on outside click (use 'click' and capture phase to avoid
      // other components stopping propagation in the bubble phase)
      if (e.type === 'click') {
        const clickedMenuContainer = e.target?.closest?.('.filter-button');
        // wrapper that contains this menu (the .filter-button for this instance)
        const myWrapper = menuRef.current?.closest?.('.filter-button');

        // If click was inside this menu (or its trigger), do nothing
        if (menuRef.current && menuRef.current.contains(e.target)) {
          return;
        }

        if (clickedMenuContainer) {
          // Click landed inside another menu's wrapper:
          // defer closing so the clicked menu's own handler can run first,
          // allowing that menu to open with the same click.
          // Only close after the other menu opens (if it's not this one).
          if (clickedMenuContainer !== myWrapper) {
            setTimeout(() => {
              // still outside this menu? then close
              if (
                menuRef.current &&
                !menuRef.current.contains(clickedMenuContainer)
              ) {
                handleToggleMenu(e, false);
              }
            }, 0);
          }
        } else {
          // True outside click — close immediately
          /* eslint-disable-next-line no-lonely-if -- meets requirements */
          if (menuRef.current && !menuRef.current.contains(e.target)) {
            handleToggleMenu(e, false);
          }
        }
      }
    }

    document.addEventListener('keydown', handleEvents, true);
    // use capture phase for clicks so we detect outside clicks even if some
    // descendant handler stops propagation in the bubble phase
    document.addEventListener('click', handleEvents, true);

    // Cleanup
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', handleEvents, true);
      document.removeEventListener('click', handleEvents, true);
    };
  }, [handleToggleMenu, menuOpen]);

  useEffect(() => {
    // listen for other menus opening and close this one if they do
    const onOtherMenuOpen = (ev) => {
      if (ev?.detail?.id && ev.detail.id !== menuId.current) {
        setMenuOpen(false);
      }
    };
    window.addEventListener('filterMenu:open', onOtherMenuOpen);
    return () => {
      window.removeEventListener('filterMenu:open', onOtherMenuOpen);
    };
  }, []);

  return (
    <div className="filter-button tw-relative tw-inline-block tw-flex-shrink-0">
      {triggerTitle ? (
        <Button
          id={`${triggerTitle}-filter-btn-wrapper`}
          ref={triggerButtonRef}
          variant="secondary"
          aria-label={triggerTitle}
          data-testid={`filter-trigger-${(triggerTitle || '').toLowerCase()}`}
          icon={<TriggerIcon color="brand" size="md" isFilled={false} />}
          onClickCapture={(e) => {
            // Prevent child handlers from hijacking tracking info
            // -- resolves issue where tracked events show non-specific
            // html element rather than the provided element/detail
            e.stopPropagation();
            handleToggleMenu(e, true);
          }}
          className={cn('tw-flex tw-h-12 tw-items-center tw-pl-4 tw-pr-6', {
            'tw-bg-blue-bright-100': isFilterApplied,
          })}
        >
          {triggerTitle}
        </Button>
      ) : (
        <IconButton
          tabIndex={0}
          ref={triggerIconButtonRef}
          id="main-filter-btn-wrapper"
          data-testid="filter-trigger-main"
          aria-label="Open Main Filter Menu"
          variant="secondary"
          onClickCapture={(e) => {
            // Prevent child handlers from hijacking tracking info
            // -- resolves issue where tracked events show non-specific
            // html element rather than the provided element/detail
            e.stopPropagation();
            handleToggleMenu(e, true);
          }}
          className={cn('tw-h-12', {
            'tw-bg-blue-bright-100': isFilterApplied,
          })}
        >
          <TriggerIcon color="brand" size="md" isFilled={false} />
        </IconButton>
      )}
      {menuOpen &&
        createPortal(
          <>
            <div
              ref={menuRef}
              id="filter"
              role="menu"
              style={anchorStyle}
              tabIndex={-1}
              aria-labelledby="menu-title"
              className="tw-fixed tw-inset-x-0 tw-bottom-0 tw-z-30 tw-mt-4 tw-flex tw-max-h-70dvh tw-w-full tw-flex-col tw-overflow-hidden tw-rounded-t-2xl tw-bg-white tw-shadow-md md_tw-absolute md_tw-bottom-auto md_tw-max-h-144 md_tw-w-96 md_tw-rounded-2xl"
            >
              <div className="tw-flex tw-h-16 tw-shrink-0 tw-items-center tw-justify-between tw-bg-blue-bright-600 tw-p-2 tw-text-white">
                <div
                  className="tw-pl-4 tw-font-medium"
                  aria-hidden="true"
                  id="menu-title"
                >
                  {menuTitle}
                </div>
                <IconButton
                  variant="primary"
                  onClickCapture={(e) => {
                    // Prevent child handlers from hijacking tracking info
                    // -- resolves issue where tracked events show non-specific
                    // html element rather than the provided element/detail
                    e.stopPropagation();
                    handleToggleMenu(e, false);
                  }}
                  tabIndex={-1}
                  aria-label="Close menu"
                >
                  <IconClose />
                </IconButton>
              </div>
              <div className="tw-min-h-0 tw-flex-1 tw-overflow-auto tw-p-2">
                {/* Menu items */}
                {children}
              </div>
              {/* Action buttons */}
              <div className="tw-shrink-0 tw-px-4">
                <hr
                  className="tw-border-t tw-border-gray-300"
                  aria-hidden="true"
                />
                <div className="tw-flex tw-justify-between tw-gap-4 tw-py-4">
                  <Button
                    variant="tertiary"
                    onClickCapture={(e) => {
                      // Prevent child handlers from hijacking tracking info
                      // -- resolves issue where tracked events show non-specific
                      // html element rather than the provided element/detail
                      e.stopPropagation();
                      customTrack({
                        event: e,
                        element: `reset ${filterTriggerTitle}filters`,
                        detail: 'filter modal',
                      });
                      onResetClick(e);
                    }}
                  >
                    {resetButtonText ?? 'Reset'}
                  </Button>
                  <Button
                    variant="primary"
                    onClickCapture={(e) => {
                      // Prevent child handlers from hijacking tracking info
                      // -- resolves issue where tracked events show generic
                      // html element rather than the provided trackingPayload
                      e.stopPropagation();
                      handleToggleMenu(e, false);
                      onApplyClick(e, {
                        element: `apply ${filterTriggerTitle}filters`,
                        detail: applyButtonText,
                      });
                    }}
                    aria-label={
                      applyButtonText ?? `apply ${filterTriggerTitle}filters`
                    }
                  >
                    {applyButtonText ?? 'Apply'}
                  </Button>
                </div>
              </div>
            </div>
            <div
              className={cn(
                'tw-fixed tw-inset-0 tw-z-20 tw-bg-gray-700 tw-opacity-70 md_tw-hidden'
              )}
              aria-hidden="true"
            />
          </>,
          document.body
        )}
    </div>
  );
};

FilterMenu.propTypes = {
  TriggerIcon: PropTypes.elementType.isRequired,
  triggerTitle: PropTypes.string,
  children: PropTypes.node.isRequired,
  isFilterApplied: PropTypes.bool,
  onResetClick: PropTypes.func,
  onApplyClick: PropTypes.func,
  onMenuClose: PropTypes.func,
  applyButtonText: PropTypes.string,
  resetButtonText: PropTypes.string,
  menuTitle: PropTypes.string.isRequired,
};

export const MenuGroup = ({ id, title, children, showTitle = true }) => {
  return (
    <ul
      id={id}
      role="listbox"
      aria-label={title}
      className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1 tw-px-0 tw-py-2"
    >
      {showTitle && title && (
        <div className="tw-heading-xs-bold tw-px-4" aria-hidden="true">
          {title}
        </div>
      )}
      {children}
    </ul>
  );
};

MenuGroup.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  showTitle: PropTypes.bool,
};

export const MenuTypes = Object.freeze({
  SELECT: 'select',
  TOGGLE: 'toggle',
});

export const MenuItem = ({
  id,
  label,
  icon,
  isSelected,
  type,
  onClick,
  hintText,
}) => {
  if (type === MenuTypes.TOGGLE) {
    return (
      <li
        id={id}
        role="option"
        aria-selected={isSelected}
        tabIndex={-1}
        className={cn(
          'tw-max-w-full tw-whitespace-normal tw-break-words tw-pl-4',
          styles.toggleItem
        )}
        onClickCapture={(e) => {
          // Prevent child handlers from hijacking tracking info
          // to resolve issue where tracked events show generic
          // html element rather than the provided element/detail
          e.stopPropagation();
          onClick(e);
        }}
      >
        <ToggleSwitch
          hint={hintText}
          id={`filter-toggle-${label}`}
          label={label}
          hideToggleIcon={true}
          hideToggleStatus={true}
          checked={isSelected}
        />
      </li>
    );
  }

  return (
    <li
      id={id}
      role="option"
      aria-selected={isSelected}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        // Activate on Enter or Space for keyboard users
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          if (onClick) onClick(e);
        }
      }}
      className={cn(
        'tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-gap-2 tw-rounded-lg tw-px-4 tw-py-2 tw-text-left hover_tw-bg-blue-bright-tints-600-t88',
        touchTargetStyles.touchTarget,
        {
          'tw-bg-blue-bright-tints-600-t88': isSelected,
        }
      )}
    >
      {icon && (
        <div className="tw-flex-shrink-0 svg-children_tw-fill-blue-bright-600">
          {icon}
        </div>
      )}
      <div className="tw-min-h-8 tw-min-w-0 tw-flex-1 tw-whitespace-normal tw-break-words tw-font-medium tw-leading-8">
        {label}
      </div>
      {isSelected && (
        <div className="tw-flex-shrink-0">
          <IconCheck size="md" isFilled={true} color="success" />
        </div>
      )}
    </li>
  );
};

MenuItem.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string.isRequired,
  icon: PropTypes.element,
  isSelected: PropTypes.bool,
  type: PropTypes.oneOf(Object.values(MenuTypes)),
  onClick: PropTypes.func,
  hintText: PropTypes.string,
};
